import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Bell, MailOpen, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 60_000;

const InboxBell = () => {
  const [unread, setUnread] = useState(0);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const pollRef = useRef(null);

  const authHeaders = () => {
    const t = localStorage.getItem("access_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const fetchUnread = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/messages/unread-count`, {
        headers: authHeaders(),
      });
      setUnread(r.data?.unread_count || 0);
    } catch (_) {
      /* silent — endpoint may 401 during logout */
    }
  }, []);

  const fetchInbox = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/messages/inbox?limit=20`, {
        headers: authHeaders(),
      });
      setMessages(r.data?.messages || []);
      setUnread(r.data?.unread_count || 0);
    } catch (_) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnread();
    pollRef.current = setInterval(fetchUnread, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    if (open) fetchInbox();
  }, [open]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/messages/${id}/read`, null, {
        headers: authHeaders(),
      });
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, is_read: true } : m))
      );
      setUnread((u) => Math.max(0, u - 1));
    } catch (_) {
      /* ignore */
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/messages/mark-all-read`, null, {
        headers: authHeaders(),
      });
      setMessages((prev) => prev.map((m) => ({ ...m, is_read: true })));
      setUnread(0);
    } catch (_) {
      /* ignore */
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const now = new Date();
      const diff = (now - d) / 1000;
      if (diff < 60) return "just now";
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      return d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    } catch {
      return iso;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative"
          data-testid="nav-inbox-bell"
          title="Inbox"
        >
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span
              data-testid="nav-inbox-unread-badge"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[11px] font-semibold flex items-center justify-center leading-none"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-96 p-0"
        data-testid="nav-inbox-popover"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Inbox className="w-4 h-4 text-slate-600" />
            <span className="text-sm font-semibold text-slate-900">Inbox</span>
            {unread > 0 && (
              <span className="text-xs text-slate-500">
                ({unread} unread)
              </span>
            )}
          </div>
          {unread > 0 && (
            <button
              onClick={markAllRead}
              data-testid="nav-inbox-mark-all-read"
              className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
            >
              <MailOpen className="w-3 h-3" />
              Mark all read
            </button>
          )}
        </div>

        <ScrollArea className="max-h-[420px]">
          {loading && messages.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500">
              Loading…
            </div>
          ) : messages.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-500">
              No messages yet
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {messages.map((m) => (
                <li
                  key={m.id}
                  data-testid={`inbox-message-${m.id}`}
                  onClick={() => !m.is_read && markRead(m.id)}
                  className={`px-4 py-3 cursor-pointer transition-colors ${
                    m.is_read
                      ? "bg-white hover:bg-slate-50"
                      : "bg-blue-50/60 hover:bg-blue-100/60"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {!m.is_read && (
                          <span className="w-2 h-2 rounded-full bg-blue-600 shrink-0" />
                        )}
                        <p className="text-sm font-semibold text-slate-900 truncate">
                          {m.subject || "(no subject)"}
                        </p>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">
                        From {m.from_name || m.from_email}
                      </p>
                      <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap line-clamp-3">
                        {m.body}
                      </p>
                    </div>
                    <span className="text-[11px] text-slate-400 shrink-0">
                      {fmtDate(m.sent_at)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default InboxBell;
